import { ChevrondownSvg, ClockIcon, Profile } from "@/src/Asset";
import { useState, useEffect } from "react";
import BlogsLayouts from "@/src/Layouts/BlogsLayouts/BlogsLayouts";
import Separator from "@/src/Components/common/Separator";
import {
  getCoursePageCache,
  setCoursePageCache,
} from "@/utils/coursePageQuery/courseDataCache";
import { apiRequest } from "@/utils/apiHelper";
import { buildQueryString } from "@/utils/buildQueryString";
import { blogsQueryParams } from "@/utils/blogsListingPageQuery/blogsQueryParams";
import { newsBlogFormatDate } from "@/utils/helpers/formatDate";
import { sanitizeHTML } from "@/utils/helpers/sanatizeHtml";
import Sidebar from "@/src/Components/blogPage/Sidebar";
import RelatedBlogPage from "@/src/Components/blogPage/RelatedLandingBlogPage";
import { Breadcrumb } from "@/src/Components/ui/common/breadCrumbs/BreadCrumbs";
import { domain_url } from "@/src/api/apiConstant";
import { generateBreadcrumbSchema } from "@/utils/schema/newsAndBlogSchema";
import Head from "next/head";
import {
  generateArticleSchema,
  generateWebPageSchema,
} from "@/utils/schema/schema";
import { generateBlogSEOData } from "@/src/utils/seo-utils/blogs/seoBlogLandingPageUtils";
import SEOHead from "@/src/Components/seo/BlogSeoHead";
import Image from "next/image";

export default function BlogsPage({ singleBlogData, blogs, blogsId }: any) {
  const relatedPosts = blogs?.results?.filter(
    (blog: { blog_url: string }) => blog.blog_url !== blogsId
  );

  const blog_url =
    blogsId &&
    singleBlogData &&
    `${domain_url}/blogs/${singleBlogData?.blog_url}`;

  const title = singleBlogData?.blog_title;
  const author_name =
    singleBlogData?.data?.updated_by_user?.name ??
    singleBlogData?.created_by_user?.name ??
    singleBlogData?.author_name;

  const [articleContent, setArticleContent] = useState<any[]>([]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [showTOC, setShowTOC] = useState(true);
  const toggleTOC = () => setShowTOC((prev) => !prev);

  const toggleReadMore = () => {
    if (isExpanded) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setTimeout(() => {
        setIsExpanded(false);
      }, 800);
    } else {
      setIsExpanded(true);
    }
  };

  useEffect(() => {
    if (!singleBlogData?.blog_content) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(
      singleBlogData.blog_content,
      "text/html"
    );

    const headings = Array.from(doc.querySelectorAll("h2, h3, h4, h5, h6"));

    const nestedHeadings: any[] = [];
    const headingStack: any[] = [];

    headings.forEach((heading, index) => {
      const tag = heading.tagName.toLowerCase();
      const level = parseInt(tag[1]); // Get heading level from tag name
      const text = heading.textContent ?? "";
      const id = `section-${index}`;
      heading.setAttribute("id", id);

      const newNode = { id, name: text, children: [] };

      // Clean stack: remove elements with equal or higher level
      while (
        headingStack.length &&
        parseInt(headingStack[headingStack.length - 1].tag[1]) >= level
      ) {
        headingStack.pop();
      }

      if (headingStack.length === 0) {
        // Top-level heading
        nestedHeadings.push(newNode);
      } else {
        // Add as a child of the last item in the stack
        const parent = headingStack[headingStack.length - 1];
        parent.node.children.push(newNode);
      }

      headingStack.push({ tag, node: newNode });
    });

    setArticleContent(nestedHeadings);

    // Insert image after the second <h2>
    const h2Tags = doc.getElementsByTagName("h2");
    if (h2Tags.length >= 2 && singleBlogData?.featured_image?.url) {
      const secondH2 = h2Tags[1];

      if (
        secondH2.nextElementSibling?.tagName !== "IMG" &&
        !secondH2.nextElementSibling?.querySelector?.("img")
      ) {
        const imgElement = doc.createElement("img");
        imgElement.src = singleBlogData.featured_image.url;
        imgElement.alt =
          singleBlogData.featured_image.name ?? "Blog Featured Image";
        imgElement.style.cssText = "margin-top: 20px; border-radius: 8px;";
        secondH2.insertAdjacentElement("afterend", imgElement);
      }
    }

    singleBlogData.blog_content = doc.body.innerHTML;
  }, [singleBlogData]);

  const renderTOC = (items: any[]) => {
    return (
      <ul className="list-disc pl-4">
        {items.map((item) => (
          <li key={item.id} className="py-1 text-sm">
            <button
              onClick={() => handleScrollToSection(item.id)}
              className="cursor-pointer font-semibold"
            >
              {item.name}
            </button>
            {item.children?.length > 0 && (
              <div className="pl-4">{renderTOC(item.children)}</div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const cleanedBlogContent = sanitizeHTML(singleBlogData?.blog_content ?? "");

  const styledBlogContent = cleanedBlogContent.replace(
    /<h2/g,
    '<h2 style="font-weight: bold; font-size: 24px; color: #334988; margin-top: 20px;"'
  );

  const handleScrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      const offsetTop =
        section.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  const breadcrumbItems = generateBreadcrumbSchema(
    title,
    blog_url,
    domain_url,
    "blogs",
    "Blogs"
  );

  const webPageSchema = generateWebPageSchema(
    singleBlogData?.blog_title,
    blog_url
  );

  const articleSchema = generateArticleSchema(
    title,
    domain_url,
    blog_url,
    author_name,
    singleBlogData?.updatedAt,
    singleBlogData?.publishedAt,
    singleBlogData?.featured_image?.url
  );

  const seoData = generateBlogSEOData(singleBlogData);

  return (
    <>
      <SEOHead seoData={seoData} />
      {domain_url === "https://collegehai.com" && blog_url && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
          />
        </Head>
      )}
      {breadcrumbItems.length > 0 && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbItems,
              }),
            }}
          />
        </Head>
      )}
      {articleSchema && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />
        </Head>
      )}
      <BlogsLayouts>
        <section>
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} color="text-black" />
          </div>

          <div className=" flex md:gap-8">
            <div className="flex flex-col gap-4">
              <div className="h-full flex flex-col gap-2 justify-end text-black">
                <h1 className="text-lg leading-tight md:text-2xl font-medium md:font-bold text-primary">
                  {singleBlogData?.blog_title}
                </h1>
                <span className="flex items-center gap-x-2 text-xs">
                  <ClockIcon className="w-5 h-5" />
                  {newsBlogFormatDate(singleBlogData?.updatedAt)}
                </span>
                <div className="text-sm text-black flex-col items-start gap-2 sm:gap-x-4">
                  {singleBlogData?.updated_by_user ??
                  singleBlogData?.created_by_user ? (
                    <div className="flex flex-col gap-1 w-auto">
                      <div className="flex items-center gap-2 ">
                        <p className="text-gray-600 text-xs">
                          {singleBlogData?.updated_by_user
                            ? "Updated By -"
                            : "Created By -"}
                        </p>
                        <Image
                          src={
                            singleBlogData?.updated_by_user?.profile_picture
                              ?.url ??
                            singleBlogData?.created_by_user?.profile_picture
                              ?.url ??
                            Profile
                          }
                          alt={
                            singleBlogData?.updated_by_user?.name ??
                            singleBlogData?.created_by_user?.name ??
                            "Author"
                          }
                          width={20}
                          height={20}
                          className="rounded-full shrink-0"
                        />
                        <span className="text-xs text-gray-800 whitespace-nowrap truncate">
                          {singleBlogData?.updated_by_user?.name ??
                            singleBlogData?.created_by_user?.name ??
                            "Unknown Author"}
                        </span>
                      </div>

                      {/* Designation Section */}
                      {singleBlogData?.updated_by_user?.designation ??
                      singleBlogData?.created_by_user?.designation ? (
                        <p className="text-gray-500 text-xs line-clamp-1">
                          <span className="font-medium text-gray-700">
                            Designation -{" "}
                          </span>
                          {singleBlogData?.updated_by_user?.designation ??
                            singleBlogData?.created_by_user?.designation}
                        </p>
                      ) : null}

                      {/* Author Bio Section */}
                      {singleBlogData?.updated_by_user?.author_bio ??
                      singleBlogData?.created_by_user?.author_bio ? (
                        <p className="text-gray-500 text-xs    ">
                          <span className="font-medium text-gray-700">
                            Author Bio -{" "}
                          </span>
                          {singleBlogData?.updated_by_user?.author_bio ??
                            singleBlogData?.created_by_user?.author_bio}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {/* Tags Section */}
                  {singleBlogData?.tags?.length > 0 && (
                    <>
                      <div className="hidden w-[0.8px] h-5 bg-gray-400"></div>
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-gray-600 ">Tags:</p>
                        <div className="flex gap-2">
                          {singleBlogData?.tags?.map((tag: any) => (
                            <p
                              key={tag?.id}
                              className="font-medium bg-tertiary rounded-full px-4 text-white py-1 text-xs"
                            >
                              {tag?.tags_name}
                            </p>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 md:gap-4 h-full px-2 lg:px-0">
                <p className="text-gray-900 md:leading-snug text-xs md:text-base">
                  {singleBlogData?.description ?? singleBlogData?.excerpt}
                </p>
                {articleContent?.length > 0 && (
                  <div className="bg-gray-100 p-4 rounded-lg overflow-hidden transition-all duration-500">
                    <div className="flex justify-between items-center mb-2 sm:mb-4">
                      <div className="font-semibold text-lg sm:text-xl">
                        Table of Content
                      </div>
                      <button
                        onClick={toggleTOC}
                        className="text-sm text-blue-600 font-medium focus:outline-none"
                      >
                        {showTOC ? "Hide" : "Show"}
                      </button>
                    </div>

                    <div
                      className={`transition-all duration-500 ease-in-out ${
                        showTOC ? "max-h-[10000px]" : "max-h-0"
                      } overflow-hidden`}
                    >
                      {renderTOC(articleContent)}
                    </div>
                  </div>
                )}

                <div className="relative">
                  {/* Page content */}
                  <div
                    dangerouslySetInnerHTML={{ __html: styledBlogContent }}
                    className={`ckeditorData  transition-all duration-300 ease-in-out ${
                      isExpanded ? "max-h-none" : "max-h-[500px] md:max-h-none"
                    } ${
                      isExpanded
                        ? "overflow-visible"
                        : "overflow-hidden md:overflow-visible"
                    }`}
                  />

                  {/* Translucent Read More Overlay for mobile only */}
                  {!isExpanded && (
                    <div className="absolute bottom-0 left-0 right-0 bg-white flex justify-center items-center md:hidden py-4">
                      <button
                        onClick={toggleReadMore}
                        className="text-white px-4 rounded-lg py-2 text-sm bg-blue-500 shadow-inner drop-shadow"
                      >
                        Read More{" "}
                        <ChevrondownSvg className="inline-block text-xl" />
                      </button>
                    </div>
                  )}
                </div>
                {isExpanded && (
                  <div className="flex items-center justify-center mb-4">
                    <button
                      onClick={toggleReadMore}
                      className="mt-1 object-center text-white px-4 rounded-lg py-2 text-sm bg-blue-500 shadow-inner drop-shadow md:hidden"
                    >
                      Read Less{" "}
                      <ChevrondownSvg className="inline-block rotate-180 text-xl" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Sidebar title={title} url={blog_url} />

          <div className="max-w-screen-xl mx-auto my-5 md:my-10 px-4">
            <Separator />
          </div>
          {/* Related post */}
          <RelatedBlogPage relatedPosts={relatedPosts} />
        </section>
      </BlogsLayouts>
    </>
  );
}

interface InMemoryCacheConfig {
  NEXT_PUBLIC_IN_MEMORY_CACHE_BLOG_PAGE?: string;
}

async function fetchData(cacheKey: string, queryParams: any, endpoint: string) {
  let inMemoryConfig: InMemoryCacheConfig = {};

  try {
    inMemoryConfig = JSON.parse(
      process.env.NEXT_PUBLIC_IN_MEMORY_CACHE_BLOG_PAGE ?? "{}"
    ) as InMemoryCacheConfig;
  } catch (error) {
    console.error("Error parsing NEXT_PUBLIC_IN_MEMORY_CACHE:", error);
  }

  const shouldUseCache =
    inMemoryConfig?.NEXT_PUBLIC_IN_MEMORY_CACHE_BLOG_PAGE === "true";

  if (shouldUseCache) {
    let data = getCoursePageCache(cacheKey);

    if (data) {
      console.log(`[Cache Hit] ${endpoint} data retrieved from cache.`);
      return data;
    }

    console.log(`[Cache Miss] Fetching ${endpoint} from API...`);
  }

  // Fetch data from API if COURSE cache is disabled or if there's a cache miss
  try {
    const response = await apiRequest(
      `/${endpoint}?${buildQueryString(queryParams)}`,
      "GET"
    );

    if (response?.data?.data?.results) {
      const data = response.data.data;
      if (shouldUseCache) {
        setCoursePageCache(cacheKey, data);
      }
      return data;
    } else {
      console.warn(`[API Error] Failed to fetch ${endpoint} data.`);
      return [];
    }
  } catch (error) {
    console.error(`[Fetch Error] Error fetching ${endpoint}:`, error);
    return [];
  }
}

export async function getServerSideProps(context: any) {
  const { blogsId } = context.params;

  const relatedBlogQueryParams = {
    ...blogsQueryParams,
    pagination: { page: 1, pageSize: 7 },
  };

  const blogs = await fetchData(
    JSON.stringify(relatedBlogQueryParams),
    relatedBlogQueryParams,
    "blogs"
  );

  const blogDataQueryParams = {
    ...blogsQueryParams,
    fields: [...blogsQueryParams.fields, "blog_content"],
    populate: {
      ...blogsQueryParams.populate,
      seo: {
        fields: [
          "page_title",
          "page_description",
          "page_og_title",
          "page_og_description",
          "twitter_title",
          "twitter_description",
          "keywords",
        ],
      },
    },
    filters: {
      blog_url: { $eq: blogsId },
    },
  };

  const blogsData = await fetchData(
    JSON.stringify(blogDataQueryParams),
    blogDataQueryParams,
    "blogs"
  );

  const singleBlogData = blogsData?.results?.[0];

  if (!singleBlogData) {
    return {
      notFound: true,
    };
  }

  // const footerData = await fetchFooterData();

  return {
    props: {
      blogs,
      singleBlogData,
      blogsId,
      // footerData,
    },
  };
}
